<template>
  <div class="flex flex-col gap-4 p-4">
    <!-- Billing Address -->
    <FormCard title="Billing Address">
      <template #header-action>
        <button
          v-if="invoice.finalized && canCopyBilling"
          type="button"
          class="text-xs text-blue-600 hover:underline"
          @click="copyBilling"
        >
          Copy Billing
        </button>
      </template>
      <AddressCard v-if="!invoice.finalized" :address="billingAddress" />
      <AddressEditor v-else v-model="billingAddress" prefix="billing" />
    </FormCard>

    <!-- Service Address -->
    <FormCard title="Service Address">
      <AddressCard v-if="!invoice.finalized" :address="serviceAddress" />
      <AddressEditor
        v-else
        v-model="serviceAddress"
        prefix="service"
        show-location-name
      />
    </FormCard>

    <!-- Summary -->
    <FormCard title="Summary">
      <TextArea
        v-model="summary"
        placeholder="Invoice summary"
        :disabled="!invoice.finalized"
      />
    </FormCard>

    <!-- Accounting Class (invoice-level) -->
    <FormCard v-if="showInvoiceAccountingClass" title="Accounting Class">
      <AccountingClassPicker
        v-model="accountingClassId"
        :disabled="!invoice.finalized"
      />
    </FormCard>

    <!-- Line Item Accounting Classes + Inventory -->
    <template v-if="showLineItemAccountingClasses || showInventoryUsages">
      <FormCard
        v-for="item in flatLineItems"
        :key="item.id"
        :title="item.unit_name"
      >
        <AccountingClassPicker
          v-if="showLineItemAccountingClasses"
          label="Accounting Class"
          :model-value="lineItemAccountingClasses[item.id] ?? null"
          :disabled="!invoice.finalized"
          @update:model-value="
            (val) => (lineItemAccountingClasses[item.id] = val)
          "
        />
        <InventoryUsages
          v-if="showInventoryUsages"
          :model-value="editableUsages[item.id] || []"
          :locations="locations"
          :default-location-id="defaultLocationId"
          :loading="inventoryLoading"
          @update:model-value="(val) => (editableUsages[item.id] = val)"
        />
      </FormCard>
    </template>

    <!-- Discount Accounting Classes -->
    <FormCard
      v-if="showLineItemAccountingClasses && hasDiscountAccountingClasses"
      title="Discount Accounting Classes"
    >
      <div
        v-for="dliac in invoice.discount_line_item_accounting_classes"
        :key="dliac.id"
        class="py-2 border-b border-gray-100 last:border-0 dark:border-gray-700"
      >
        <AccountingClassPicker
          :model-value="dliac.accounting_class_id"
          :disabled="!invoice.finalized"
          @update:model-value="(val) => (dliac.accounting_class_id = val)"
        />
      </div>
    </FormCard>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";
import { useAccessRole } from "@/lib/useAccessRole";

import type { EditableAddress } from "@/components/QuoteInvoice/AddressEditor.vue";
import AddressEditor from "@/components/QuoteInvoice/AddressEditor.vue";
import AddressCard from "@/components/Seranova/AddressCard/AddressCard.vue";
import FormCard from "@/components/Seranova/FormCard/FormCard.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import type { InventoryLocation } from "@/typings/inventory";
import type { Invoice, InvoiceLineItem } from "@/typings/invoice";
import AccountingClassPicker from "./AccountingClassPicker.vue";
import type { EditableUsage } from "./InventoryUsage.vue";
import InventoryUsages from "./InventoryUsages.vue";

const props = defineProps<{
  invoice: Invoice;
}>();

const emit = defineEmits<{
  "choose-guarantor": [];
}>();

const store = useStore();
const { canAccess } = useAccessRole();

const showInvoiceAccountingClass = computed(
  () => props.invoice.finalized && store.getters.trackClassesByInvoice
);
const showLineItemAccountingClasses = computed(
  () => props.invoice.finalized && store.getters.trackClassesByLineItem
);
const hasDiscountAccountingClasses = computed(
  () => !!props.invoice.discount_line_item_accounting_classes?.length
);
const showInventoryUsages = computed(
  () =>
    store.getters.featureEnabledForTenant("inventory") && canAccess("inventory")
);

const summary = ref(props.invoice.summary || "");
const accountingClassId = ref<number | null>(
  props.invoice.accounting_class_id ?? null
);

const serviceAddress = ref<EditableAddress>({
  name: props.invoice.service_address?.name || "",
  street: props.invoice.service_address?.street || "",
  unit: props.invoice.service_address?.unit || "",
  city: props.invoice.service_address?.city || "",
  state: props.invoice.service_address?.state || "",
  zip_code: props.invoice.service_address?.zip_code || ""
});

const billingAddress = ref<EditableAddress>({
  street: props.invoice.billing_address?.street || "",
  unit: props.invoice.billing_address?.unit || "",
  city: props.invoice.billing_address?.city || "",
  state: props.invoice.billing_address?.state || "",
  zip_code: props.invoice.billing_address?.zip_code || ""
});

const flatLineItems = computed<InvoiceLineItem[]>(() => {
  const items: InvoiceLineItem[] = [];
  for (const item of props.invoice.items_attributes) {
    if (item.items_in_group?.length) {
      items.push(...item.items_in_group);
    } else {
      items.push(item);
    }
  }
  return items;
});

const lineItemAccountingClasses = reactive<Record<number, number | null>>({});

onMounted(() => {
  for (const item of flatLineItems.value) {
    lineItemAccountingClasses[item.id] = item.accounting_class_id ?? null;
  }

  if (showInvoiceAccountingClass.value || showLineItemAccountingClasses.value) {
    store.dispatch("fetchAccountingClasses");
  }
});

const editableUsages = reactive<Record<number, EditableUsage[]>>({});
const locations = ref<InventoryLocation[]>([]);
const defaultLocationId = ref<number | null>(null);
const inventoryLoading = ref(false);
const inventoryFetched = ref(false);

const fetchInventoryData = async () => {
  if (inventoryFetched.value) return;
  if (!showInventoryUsages.value) return;
  if (!props.invoice.links?.invoice_product_usage) return;

  inventoryFetched.value = true;
  inventoryLoading.value = true;

  try {
    const [usageData, locs] = await Promise.all([
      store.dispatch("fetchInventoryInvoiceProductUsage", {
        url: props.invoice.links.invoice_product_usage
      }),
      store.dispatch("fetchInventoryLocations")
    ]);

    locations.value = locs;
    defaultLocationId.value = usageData.default_inventory_location_id ?? null;

    const rawUsages = usageData.inventory_usages || [];
    for (const item of flatLineItems.value) {
      editableUsages[item.id] = rawUsages
        .filter((u) => u.invoice_line_item_id === item.id)
        .map((u) => ({
          part_id: u.part_id,
          part_name: u.part?.name || "",
          inventory_location_id: u.inventory_location_id,
          quantity: u.quantity
        }));
    }
  } finally {
    inventoryLoading.value = false;
  }
};

onMounted(fetchInventoryData);
watch(showInventoryUsages, (val) => {
  if (val) fetchInventoryData();
});

const canCopyBilling = computed(() => {
  const original = props.invoice.original_billing_customer;
  const inferred = props.invoice.inferred_billing_customer;
  return inferred?.id && inferred.id !== original?.id;
});

const copyBilling = () => {
  const inferred = props.invoice.inferred_billing_customer;
  if (
    !inferred ||
    inferred.id === props.invoice.original_billing_customer?.id
  ) {
    emit("choose-guarantor");
    return;
  }
  // Copy from inferred billing customer's primary address
  // The parent page handles the full guarantor selection flow
  emit("choose-guarantor");
};

const buildSavePayload = () => {
  const payload: Record<string, unknown> = {
    summary: summary.value,
    service_address_name: serviceAddress.value.name,
    service_address_street: serviceAddress.value.street,
    service_address_unit: serviceAddress.value.unit,
    service_address_city: serviceAddress.value.city,
    service_address_state: serviceAddress.value.state,
    service_address_zip_code: serviceAddress.value.zip_code,
    billing_address_street: billingAddress.value.street,
    billing_address_unit: billingAddress.value.unit,
    billing_address_city: billingAddress.value.city,
    billing_address_state: billingAddress.value.state,
    billing_address_zip_code: billingAddress.value.zip_code
  };

  if (showInvoiceAccountingClass.value) {
    payload.accounting_class_id = accountingClassId.value;
  }

  const hasAccountingClassChanges = showLineItemAccountingClasses.value;
  const hasInventory = showInventoryUsages.value;

  if (hasAccountingClassChanges || hasInventory) {
    const lineItems = flatLineItems.value.map((item) => {
      const entry: Record<string, unknown> = { id: item.id };

      if (hasAccountingClassChanges) {
        entry.accounting_class_id = lineItemAccountingClasses[item.id];
      }

      if (hasInventory) {
        entry.inventory_usages = (editableUsages[item.id] || [])
          .filter((u) => u.part_id)
          .map((u) => ({
            part_id: u.part_id,
            inventory_location_id: u.inventory_location_id,
            quantity: u.quantity
          }));
      }

      return entry;
    });

    payload.line_items = lineItems;
  }

  if (hasDiscountAccountingClasses.value) {
    payload.discount_line_item_accounting_classes =
      props.invoice.discount_line_item_accounting_classes!.map((dliac) => ({
        id: dliac.id,
        accounting_class_id: dliac.accounting_class_id
      }));
  }

  return payload;
};

const save = async () => {
  const link = props.invoice.links?.update_details;
  if (!link) return;

  await store.dispatch("updateInvoiceDetails", {
    link,
    data: buildSavePayload()
  });

  EventBus.$emit("refresh-invoice");
};

defineExpose({ save });
</script>
